import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleBasedHeader from '../components/ui/RoleBasedHeader';
import Icon from '../components/AppIcon';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

const TeamAccess = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [ownerInvites, setOwnerInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(true);
  const [inviteType, setInviteType] = useState('employee');
  const [inviteContact, setInviteContact] = useState('');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login-authentication');
    }
  }, [user, loading, navigate]);

  const loadOwnerInvites = useCallback(async () => {
    if (!user?.id) { setOwnerInvites([]); setInvitesLoading(false); return; }
    setInvitesLoading(true);
    const { data } = await supabase
      ?.from('business_account_links')
      ?.select('*')
      ?.eq('owner_user_id', user?.id)
      ?.order('created_at', { ascending: false });
    setOwnerInvites(data || []);
    setInvitesLoading(false);
  }, [user]);

  useEffect(() => {
    loadOwnerInvites();
  }, [loadOwnerInvites]);

  const handleSendInvite = async () => {
    const contact = inviteContact.trim();
    if (!contact) { setInviteError('Enter a mobile number or email'); return; }
    const isEmail = contact.includes('@');
    const isMobile = /^\d{10}$/.test(contact.replace(/\D/g, ''));
    if (!isEmail && !isMobile) { setInviteError('Enter a valid 10-digit mobile number or email address'); return; }

    setInviteSubmitting(true);
    setInviteError('');
    setInviteSuccess('');
    try {
      const { error } = await supabase.from('business_account_links').insert({
        owner_user_id: user?.id,
        relation_type: inviteType,
        invited_mobile: isMobile ? ('+91' + contact.replace(/\D/g, '').slice(-10)) : null,
        invited_email: isEmail ? contact.toLowerCase() : null,
        status: 'pending',
      });
      if (error) throw error;
      setInviteContact('');
      setInviteSuccess('Invite sent.');
      loadOwnerInvites();
    } catch (err) {
      setInviteError(err?.message || 'Could not send invite. Please try again.');
    } finally {
      setInviteSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <RoleBasedHeader />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <RoleBasedHeader />

      <div className="max-w-2xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/buyer-operations-dashboard')}
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-600 mb-6"
        >
          <Icon name="ArrowLeft" size={15} />
          Back to Dashboard
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#eff6ff' }}>
            <Icon name="Users" size={20} color="#1e4a72" />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: '#1e4a72' }}>Team Access</h1>
        </div>
        <p className="text-sm text-gray-500 mb-8">
          Invite people to your account. Employees get operational access with DO Finance blocked; partners get read-only visibility into DO Finance.
        </p>

        {/* Invite form */}
        <div className="bg-white rounded-2xl border shadow-sm p-6 mb-6" style={{ borderColor: '#e5e7eb' }}>
          <h2 className="text-sm font-semibold mb-4" style={{ color: '#1e4a72' }}>Send a New Invite</h2>

          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setInviteType('employee')}
              className="flex-1 px-4 py-3 rounded-xl text-sm font-semibold"
              style={
                inviteType === 'employee'
                  ? { backgroundColor: '#fffbeb', border: '2px solid #d97706', color: '#92400e' }
                  : { backgroundColor: '#fafaf9', border: '1.5px solid #e5e7eb', color: '#6b7280' }
              }
            >
              🧑‍💼 Employee
            </button>
            <button
              onClick={() => setInviteType('partner')}
              className="flex-1 px-4 py-3 rounded-xl text-sm font-semibold"
              style={
                inviteType === 'partner'
                  ? { backgroundColor: '#fffbeb', border: '2px solid #d97706', color: '#92400e' }
                  : { backgroundColor: '#fafaf9', border: '1.5px solid #e5e7eb', color: '#6b7280' }
              }
            >
              🤝 Partner / Director
            </button>
          </div>

          <input
            value={inviteContact}
            onChange={e => { setInviteContact(e.target.value); setInviteError(''); setInviteSuccess(''); }}
            placeholder="Mobile number or email"
            className="w-full px-4 py-3 text-sm border rounded-xl mb-3"
            style={{ borderColor: '#e5e7eb' }}
          />

          {inviteError && (
            <div className="mb-3 px-4 py-2.5 rounded-lg text-sm" style={{ backgroundColor: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626' }}>
              {inviteError}
            </div>
          )}
          {inviteSuccess && (
            <div className="mb-3 px-4 py-2.5 rounded-lg text-sm" style={{ backgroundColor: '#f0fdf4', border: '1px solid #86efac', color: '#15803d' }}>
              {inviteSuccess}
            </div>
          )}

          <button
            onClick={handleSendInvite}
            disabled={inviteSubmitting}
            className="w-full py-3 rounded-xl text-sm font-bold text-white"
            style={{
              background: inviteSubmitting ? '#9ca3af' : 'linear-gradient(135deg, #d97706, #b45309)',
              cursor: inviteSubmitting ? 'not-allowed' : 'pointer',
            }}
          >
            {inviteSubmitting ? 'Sending...' : 'Send Invite'}
          </button>
        </div>

        {/* Existing invites */}
        <div className="bg-white rounded-2xl border shadow-sm overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
          <div className="px-6 py-4 border-b" style={{ borderColor: '#f3f4f6', backgroundColor: '#fffbf5' }}>
            <h2 className="text-sm font-semibold" style={{ color: '#1e4a72' }}>
              Your Team {ownerInvites.length > 0 && `(${ownerInvites.length})`}
            </h2>
          </div>
          <div className="p-6">
            {invitesLoading ? (
              <div className="flex justify-center py-6">
                <div className="w-6 h-6 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : ownerInvites.length === 0 ? (
              <p className="text-sm text-center py-4" style={{ color: '#9ca3af' }}>
                No team members invited yet.
              </p>
            ) : (
              <div className="space-y-2">
                {ownerInvites.map(inv => (
                  <div key={inv.id} className="flex items-center justify-between px-4 py-3 rounded-xl" style={{ backgroundColor: '#fafaf9', border: '1px solid #f3f4f6' }}>
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{inv.relation_type === 'employee' ? '🧑‍💼' : '🤝'}</span>
                      <div>
                        <p className="text-sm font-semibold" style={{ color: '#374151' }}>
                          {inv.invited_mobile || inv.invited_email}
                        </p>
                        <p className="text-xs" style={{ color: '#9ca3af' }}>
                          {inv.relation_type === 'employee' ? 'Employee' : 'Partner / Director'}
                        </p>
                      </div>
                    </div>
                    <span
                      className="px-3 py-1 rounded-full text-xs font-semibold"
                      style={
                        inv.status === 'active'
                          ? { backgroundColor: '#f0fdf4', color: '#16a34a' }
                          : { backgroundColor: '#fef3c7', color: '#92400e' }
                      }
                    >
                      {inv.status === 'active' ? 'Active' : 'Pending'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamAccess;
